import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import { parseDateSafe, getOrdinalSuffix } from "./dateFormatters";

// Utility function to combine class names (similar to clsx/classnames)
export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

// --- Location resolution -----------------------------------------------------
// The viewer's currently-selected location (top-bar account/location switcher).
export const getCurrentLocationId = () =>
  JSON.parse(sessionStorage.getItem("current_loc") || "null")?.id;

// The PATIENT's OWN location, captured when a patient's detail is loaded
// (see setPatientLocationId callers). Mirrors the existing `_pt_id` convention.
// Monitoring sessions must be stored/read under the patient's location, NOT the
// viewer's current location, otherwise sessions saved while the switcher points
// at a different location get split out of the patient's location-filtered
// totals (SCRUM-157).
export const setPatientLocationId = (id) => {
  if (id != null) sessionStorage.setItem("_pt_loc_id", String(id));
  else sessionStorage.removeItem("_pt_loc_id");
};

// Safe fallback: if the patient location was never captured (or the patient
// record has no location.id), behave exactly as before by falling back to the
// current location, so saves can never break.
export const getPatientLocationId = () =>
  sessionStorage.getItem("_pt_loc_id") || getCurrentLocationId();

export function convertUTCToLocal(
  utcString,
  locale = navigator.language,
  excludeSeconds = false,
  numericDateOnly = false
) {
  if (!utcString) return "--";

  const date = parseDateSafe(utcString);
  if (!date) return "--";

  if (numericDateOnly) {
    return date
      .toLocaleDateString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  }

  return date.toLocaleString(locale, {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
    ...(excludeSeconds ? {} : { second: "2-digit" }),
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// return date in "Apr 7th 2025" format
export function formatDate(dateObject) {
  if (!dateObject) return "--";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  let date;
  // Check if the string likely specifies UTC/timezone (contains 'T' or 'Z')
  if (/\T|\Z/.test(dateObject)) {
    // Parse as-is (original UTC-assuming behavior)
    date = new Date(dateObject);
  } else {
    // Treat as simple local date: split and create local midnight Date
    const [year, month, day] = dateObject.split("-").map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return "--"; // Invalid date string
    }
    date = new Date(year, month - 1, day);
  }

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  return `${months[monthIndex]} ${day}${getOrdinalSuffix(day)}, ${year}`;
}

// return date in "February 2026" format
export function formatMonthYear(dateObject) {
  if (!dateObject) return "--";
  const date = parseDateSafe(dateObject);
  if (!date) return "--";
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

// for simple date

export function formatSimpleDate(dateObject) {
  if (!dateObject) return "--";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  // Split and create local Date to avoid UTC shift
  const [year, month, day] = dateObject.split("-").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return "--"; // Invalid date string
  }
  const date = new Date(year, month - 1, day);
  const formattedDay = date.getDate();
  const formattedMonth = months[date.getMonth()];
  const formattedYear = date.getFullYear();

  return `${formattedMonth} ${formattedDay}${getOrdinalSuffix(
    formattedDay
  )}, ${formattedYear}`;
}

export function formatDob(dateString) {
  if (!dateString) return "--";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Split manually instead of using new Date()
  const [year, month, day] = dateString.split("-");
  const dayNumber = parseInt(day, 10);

  return `${months[parseInt(month, 10) - 1]} ${dayNumber}${getOrdinalSuffix(
    dayNumber
  )}, ${year}`;
}

// return in format "May 29th, 2025 at 12:01 PM"
export function formatDateWithTime(dateObject) {
  if (!dateObject) return "--";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = parseDateSafe(dateObject);
  if (!date) return "--";
  const day = date.getDate();
  const month = months[date.getMonth()];

  // Format hours/minutes with AM/PM
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 or 24 to 12 for 12-hour format

  return `${month} ${day} at ${hours}:${minutes} ${ampm}`;
}

// return date in "2025-02-10" format
export function formatISODate(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error("Invalid date object");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
  const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits

  return `${year}-${month}-${day}`;
}

// return date in UTC String
export function getISOString(date) {
  return new Date(date).toISOString();
}

export function formatNumber(type, number, options = {}) {
  const formatOptions = {
    style: type,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options, // merge any additional options like currency or unit
  };

  return new Intl.NumberFormat(navigator.language, formatOptions).format(
    number
  );
}

export function capitalize(str) {
  return str
    .split(/[^a-zA-Z]/) // Split by non-alphabetic characters
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(" ");
}

// Title-cases a string regardless of source casing, e.g. "WALKER" -> "Walker",
// "abdullahal mahmood" -> "Abdullahal Mahmood". Unlike `capitalize` above, this
// lowercases the remainder of each word and splits only on whitespace, so it
// won't mangle punctuation (e.g. "O'Brien").
export function toTitleCase(str) {
  if (!str) return "";
  return str
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

// Splits a {firstName, lastName, fullName} bag into {first, last}, preferring
// the explicit fields and falling back to splitting fullName (first token =
// first name, remaining tokens = last name) when they're both absent.
function splitName({ firstName, lastName, fullName } = {}) {
  if (firstName || lastName) {
    return { first: firstName || "", last: lastName || "" };
  }
  const tokens = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return { first: tokens[0] || "", last: tokens.slice(1).join(" ") };
}

// Builds a Title-Cased "Last, First" display name from {firstName, lastName,
// fullName}. Falls back gracefully when only one part is available, and to
// "--" when nothing is available. Display-only - never mutates stored data.
export function formatLastFirstName(nameFields) {
  const { first, last } = splitName(nameFields);
  const titleFirst = toTitleCase(first);
  const titleLast = toTitleCase(last);
  if (titleLast && titleFirst) return `${titleLast}, ${titleFirst}`;
  return titleLast || titleFirst || "--";
}

// Case-insensitive comparator over {firstName, lastName, fullName} bags:
// last name first, first name as a tiebreaker. Returns a normal compareFn
// result (negative/0/positive).
export function compareByLastName(aNameFields, bNameFields) {
  const a = splitName(aNameFields);
  const b = splitName(bNameFields);
  const lastCmp = a.last.toLowerCase().localeCompare(b.last.toLowerCase());
  if (lastCmp !== 0) return lastCmp;
  return a.first.toLowerCase().localeCompare(b.first.toLowerCase());
}

// Adapts a billing-summary patient row (patient_first_name / patient_last_name
// / patient_name - the shape every billing-summary payload variant returns)
// to the generic {firstName, lastName, fullName} shape the helpers above
// expect.
export function patientNameFields(patient) {
  return {
    firstName: patient?.patient_first_name,
    lastName: patient?.patient_last_name,
    fullName: patient?.patient_name
  };
}

export function displayDate(date) {
  if (date == null) return null;

  let dateObj;
  // Check if the string likely specifies UTC/timezone (contains 'T' or 'Z')
  if (/\T|\Z/.test(date)) {
    // Parse as-is (original UTC-assuming behavior)
    dateObj = new Date(date);
  } else {
    // Treat as simple local date: split and create local midnight Date
    const [year, month, day] = date.split("-").map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null; // Invalid date string
    }
    dateObj = new Date(year, month - 1, day);
  }

  const formattedMonth = String(dateObj.getMonth() + 1).padStart(2, "0");
  const formattedDay = String(dateObj.getDate()).padStart(2, "0");
  const formattedYear = dateObj.getFullYear().toString().slice(-4);
  return `${formattedMonth}-${formattedDay}-${formattedYear}`;
}

// return date as it is from backend in "mm-dd-yyyy" format
export function displayDateAsIs(dateTimeString) {
  if (!dateTimeString) return null;
  const [datePart] = dateTimeString.split("T");
  const [year, month, day] = datePart.split("-");
  return `${month}-${day}-${year}`;
}

export const formatSecondsToTime = (totalSeconds) => {
  if (!totalSeconds && totalSeconds <= 0) return "--";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const padNumber = (num) => num.toString().padStart(2, "0");

  return `${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`;
};

export async function getPageBinary(element) {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    removeContainer: true,
    ignoreElements: (el) =>
      el.style.display === "none" || el.tagName.toLowerCase() === "script",
  });

  const A4_WIDTH_PT = 595.28;
  const A4_HEIGHT_PT = 841.89;

  const pxPerPt = canvas.width / A4_WIDTH_PT;
  const marginPt = 10;
  const marginPx = marginPt * pxPerPt;

  const contentHeightPx = A4_HEIGHT_PT * pxPerPt - 2 * marginPx;
  const contentWidthPt = A4_WIDTH_PT - 2 * marginPt;

  const pdfDoc = await PDFDocument.create();

  let yPx = 0;
  const totalHeightPx = canvas.height;

  while (yPx < totalHeightPx) {
    const remainingPx = totalHeightPx - yPx;
    const isLast = remainingPx <= contentHeightPx;

    const sliceHeightPx = Math.min(contentHeightPx, remainingPx);
    const sliceHeightPt = sliceHeightPx / pxPerPt;

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;

    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0,
      yPx,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const imgData = sliceCanvas.toDataURL("image/png");
    const pngImage = await pdfDoc.embedPng(imgData);
    const page = pdfDoc.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);

    const drawY = isLast
      ? A4_HEIGHT_PT - marginPt - sliceHeightPt // align to top
      : marginPt; // center normal slices

    page.drawImage(pngImage, {
      x: marginPt,
      y: drawY,
      width: contentWidthPt,
      height: sliceHeightPt,
    });

    yPx += sliceHeightPx;
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  return blob;
}

export const generateYears = (startYear = 1900) => {
  const currentYear = new Date().getFullYear();
  if (typeof startYear !== "number" || startYear > currentYear) return [];
  return Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );
};

export const USANumberFormat = (number) => {
  if (!number) return "";
  return number.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");
};
